import type { ContactProfile } from '@common/types/contacts';
import { formatContactFormFieldDisplayValue } from '@common/utils/intakeFieldFormatting';
import { Grid, Stack, Typography } from '@mui/material';
import ReadOnlyTextField from '@/components/ReadOnlyTextField';

interface Props {
    profile: ContactProfile;
    isPrimary: boolean;
}

const PROFILE_FIELDS: { key: keyof ContactProfile; label: string }[] = [
    { key: 'firstName', label: 'First name' },
    { key: 'middleName', label: 'Middle name' },
    { key: 'lastName', label: 'Last name' },
    { key: 'contactEmail', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone' },
    { key: 'homePhone', label: 'Home phone' },
    { key: 'homeAddress', label: 'Home address' },
    { key: 'homeUnit', label: 'Unit' },
    { key: 'dateOfBirth', label: 'Date of birth' },
    { key: 'gender', label: 'Gender' },
    { key: 'preferredPronouns', label: 'Preferred pronouns' },
    { key: 'maritalStatus', label: 'Marital status' },
    { key: 'citizenshipStatus', label: 'Citizenship status' },
    { key: 'occupation', label: 'Occupation' },
    { key: 'employer', label: 'Employer' },
    { key: 'employerAddress', label: 'Employer address' },
    { key: 'employerUnit', label: 'Employer unit' },
    { key: 'employerPhone', label: 'Employer phone' },
];

export default function ContactProfileSection({ profile, isPrimary }: Props) {
    const fields = PROFILE_FIELDS.filter((field) => {
        const value = profile[field.key];
        return value !== null && value !== undefined && String(value).trim() !== '';
    });

    return (
        <Stack
            spacing={2}
            p={3}
            sx={{
                border: '1px solid',
                borderColor: 'neutral.borderColor',
                borderRadius: '4px',
                backgroundColor: 'neutral.white',
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h3">Contact Information</Typography>
                <Typography variant="body2" color="text.secondary">
                    {isPrimary ? 'Primary contact' : 'Related contact'}
                </Typography>
            </Stack>
            {fields.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No contact details available.
                </Typography>
            ) : (
                <Grid container spacing={2}>
                    {fields.map((field) => (
                        <Grid item key={field.key} xs={12} sm={6} md={4}>
                            <ReadOnlyTextField
                                fullWidth
                                label={field.label}
                                value={formatContactFormFieldDisplayValue(
                                    field.key,
                                    profile[field.key],
                                )}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Stack>
    );
}
